import { useContext, useState } from 'react';
import { ArrowLeft, Menu as MenuIcons } from 'lucide-react';

import { NavbarIconLogo } from '@/constants';
import { useFont, useGetArticle, useTitle } from './Article.hooks';
import { Loading } from './Loading';
import cssStyles from './Article.module.css';
import { ArticleContext } from './Article.context';
import { AppContext } from '@/App.context';

import { TrackingContext as TrackerContext } from '@/tracking/Tracking.context';
import { Menu } from '@/components/menu/Menu';
import { MenuButton as MenuBtn } from '@/components/menu/Button';
import { ArticleButton } from './menu-button/MenuButton';
import { TrackingData } from '@/tracking/Tracking.events';
import config from '@/config';
import { useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { SingleArticle } from '@/components/article/Articles';

export const ArticleView = () => {
	const { lang } = useContext(AppContext);
	const { sendEvent } = useContext(TrackerContext);
	const [language] = useState<string>(lang);
	const { fontSize, setFontSize } = useFont();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const { isLoading, article } = useGetArticle();
	useTitle({
		article,
		lang,
	});

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	// const languages = useLanguages(article);
	if (isLoading || !article) {
		return <Loading />;
	}

	return (
		<ArticleContext.Provider
			value={{
				article,
				language,
				fontSize,
				setFontSize,
				toggleMenu,
			}}
		>
			<View />
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				<ArticleButton.ShareArticle
					article={article}
					language={language}
					onShare={(article, method = 'os') => {
						const eventData: TrackingData = {
							method,
							contentType: 'article',
							article: {
								id: parseInt(`${article.id}`, 10),
								ArticleID: parseInt(`${article.ArticleID}`, 10),
							},
						};
						sendEvent({
							type: 'SHARE',
							data: eventData,
						});
					}}
				/>
				<ArticleButton.Library />
				<ArticleButton.Font
					fontSize={fontSize}
					setFontSize={setFontSize}
				/>
				<ArticleButton.Links links={config?.app?.menu?.links} />
				<MenuBtn.Subscribe />
				<MenuBtn.ManageSubscription />
				<MenuBtn.SignIn />
				<MenuBtn.SignUp />
				<MenuBtn.SignOut />
			</Menu>
		</ArticleContext.Provider>
	);
};

function View() {
	const { article } = useContext(ArticleContext);

	if (!article) {
		return null;
	}

	return (
		<div className={cssStyles['view']}>
			<Topbar />
			<Main />
		</div>
	);
}

function Topbar() {
	return (
		<nav className={cssStyles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={cssStyles['logo']}>
				<NavbarIconLogo />
			</div>
			<div>
				<MenuButton />
			</div>
		</nav>
	);
}

function MenuButton() {
	const { toggleMenu } = useContext(ArticleContext);
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Actions'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<MenuIcons color="#c3c3c3" />
		</button>
	);
}

function Main() {
	const { article, language, fontSize } = useContext(ArticleContext);
	if (!article) return null;
	const articleStyles = article.styles ? article.styles : [];
	return (
		<main className={cssStyles['container']}>
			<SingleArticle
				fontSize={fontSize}
				article={article}
				styles={articleStyles}
				language={language}
			/>
		</main>
	);
}

const BackButton = () => {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate();
	const { article } = useContext(ArticleContext);
	if (!article) {
		return null;
	}

	return (
		<button
			id="back-button"
			type="button"
			className="tooltip down"
			data-text="Back"
			onClick={() => {
				if (canGoBack) {
					router.history.back();
					return;
				}
				navigate({ to: '/' });
			}}
		>
			<ArrowLeft color={'#c3c3c3'} />
		</button>
	);
};
